import { once, noop } from 'lodash';
import { createNoopController } from '../../viewer-script/controllers/createNoopController';
import { validateLanguage } from '../../common/services/validate-language';
import { createLogger, getEnvironment } from '../../common/controller/helpers';
import { createReduxStore } from './create-redux-store';
import { handleProvisioning } from '../../common/services/handle-provisioning';
import { handleError } from '../../common/store/debug-state/handle-error';
import {
  initializeActions,
  initializePromisifiedActions,
  initializeStoreBaseData,
  refreshDataOnLogin,
} from './init-actions';
import { TAG_CLOUD_WIDGET_NAME } from '../../viewer-script/constants/widgets';
import { createMonitoring } from '../../common/services/create-monitoring';
import { subscribeToChange } from '../../common/services/state-optimizer';
import { simulateControllerError } from '../../common/services/simulate-error';
import { isInWorker } from '../../common/services/is-in-worker';
import { isRtlLanguage } from '../../common/services/is-rtl-language';
import listenToEditModeChange from '../../common/services/listen-to-edit-mode-change';
import listenToSettingsChange from '../../common/services/listen-to-settings-change';
import { getInitialStateVersions } from '../../common/services/state-optimizer/change-detector';
import fetchTags from '../../common/store/tags/fetch-tags';

const isProduction = process.env.NODE_ENV === 'production';
let isControllerInitialized = false;

export const createTagCloudController = (
  { appParams, compId, config, setProps, wixCodeApi, platformAPIs, type },
  allCtrls,
) => {
  const { isEditor, isPreview, isSSR, isDebug } = getEnvironment(wixCodeApi);

  // there is a bug in viewerScriptWrapper where it initialized controller again when going into preview
  if ((isEditor || isPreview) && (isControllerInitialized || isInWorker())) {
    return createNoopController();
  } else {
    isControllerInitialized = true;
  }

  const { captureToSentry, fedopsLogger } = createMonitoring({
    origin: `app-worker-${TAG_CLOUD_WIDGET_NAME}`,
    platformAPIs,
    appParams,
    isSSR,
    isDebug,
    type,
  });

  fedopsLogger.appLoadStarted();
  const fedopsAppLoaded = once(() => fedopsLogger.appLoaded());

  const language = validateLanguage(wixCodeApi.site.language);

  const log = createLogger(isDebug, isProduction);

  log('createTagCloudController', { appParams, allCtrls, wixCodeApi, isSSR, language });

  const pageReady = async () => {
    let store;
    try {
      log('createTagCloudController.pageReady -> start');

      simulateControllerError(wixCodeApi, 'tag-cloud.pageReady');

      store = createReduxStore({
        appParams,
        wixCodeApi,
        compId,
        fedopsLogger,
        isEditor,
        isPreview,
      });
      const actions = initializeActions({
        wixCodeApi,
        store,
        fedopsLogger,
        fedopsAppLoaded,
      });
      const actionsPromisified = initializePromisifiedActions({ wixCodeApi, compId, store });

      await initializeStoreBaseData({
        wixCodeApi,
        appParams,
        store,
        language,
        platformAPIs,
        config,
        bundleName: 'tag-cloud-widget',
        fedopsAppName: 'communities-blog-tag-cloud-widget',
        translationsName: 'tag-cloud-widget',
      });

      await store.dispatch(fetchTags());

      log('createTagCloudController.pageReady -> done');
      const state = store.getState();

      const stateVersions = getInitialStateVersions(state);
      setProps({
        state,
        stateVersions,
        actions,
        actionsPromisified,
        cssBaseUrl: appParams.baseUrls.staticsBaseUrl,
        isSSR,
        isRTL: isRtlLanguage(language),
      });

      if (isSSR) {
        fedopsAppLoaded();
      }

      if (isEditor || isPreview) {
        listenToEditModeChange(store);
        listenToSettingsChange(store);
      }

      refreshDataOnLogin({ wixCodeApi, store, router: undefined, pathname: undefined, doRouting: noop });
      subscribeToChange(store, stateVersions, setProps);
    } catch (error) {
      handleError({
        controller: TAG_CLOUD_WIDGET_NAME,
        store,
        setProps,
        appParams,
        captureToSentry,
        isDebug,
        isSSR,
      })(error);
    }
  };

  return Promise.resolve({
    pageReady: () =>
      isEditor ? handleProvisioning(appParams, fedopsLogger, wixCodeApi, setProps, pageReady) : pageReady(),
    exports: () => {},
  }).catch(console.error);
};
